import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';

import styles from './Dashboard.module.css';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import AuthPage from '../components/AuthPage';
import GearIcon from '../components/GearIcon';
import { useStore } from '../lib/frontend/store';
import {
  ProjectsApiDerivedResponse,
  TopicDifficulty,
  TopicRole,
} from '../lib/universal/questions';
import { sendPageHitEvent } from '../lib/frontend/pixel';
import { useAuth } from '../lib/frontend/auth';
import Spinner from '../components/Spinner';
import PaymentDueBanner from '../components/PaymentDueBanner';
import ReactivateSubscriptionBanner from '../components/ReactivateSubscriptionBanner';
import InstrumentedLink from '../components/InstrumentedLink';
import { CATEGORIES } from '../lib/universal/types';
import {
  capitalise,
  pluralise,
  projectRoleLabel,
  timeToHoursAndMinutesString,
  uniqueStrings,
} from '../lib/universal/util';
import ProjectProgressBar from '../components/ProjectProgressBar';
import { getLS, setLS } from '../lib/frontend/persist';

const ProjectCard: React.FC<{
  project: ProjectsApiDerivedResponse['projects'][number];
  topic: ProjectsApiDerivedResponse['topics'][number];
}> = ({ project, topic }) => {
  const router = useRouter();

  const onClickTopicButton = useCallback(() => {
    router.push({
      pathname: '/project',
      query: { projectId: topic.id },
    });
  }, [topic, router]);

  const timeRemaining = useMemo(
    () => timeToHoursAndMinutesString(project.timeEstimateRemaining),
    [project.timeEstimateRemaining]
  );

  const tags = useMemo(
    () =>
      uniqueStrings(
        topic.roles
          .filter((r) => r !== 'all')
          .map((r) => projectRoleLabel(r))
          .concat(topic.tags)
          .concat(topic.skills)
      ),
    [topic]
  );

  return (
    <Col md={4} sm={6} xs={12} className={styles.projectCard}>
      <div className={styles.projectIconContainer}>
        <div className={styles.projectIcon}>
          <img src={`/topic-icons/${project.icon}.png`} alt={project.label} />
        </div>
      </div>
      <div className={styles.projectCardInner} onClick={onClickTopicButton}>
        {project.isFree ? (
          <div className={styles.freeProjectTag}>
            <img src="/free.png" />
          </div>
        ) : null}
        <Row>
          <Col className={styles.projectHeading}>{project.label}</Col>
        </Row>
        <Row>
          <Col className={styles.projectDescription}>{topic.description}</Col>
        </Row>
        <Row>
          <Col className={styles.projectDifficulty}>
            <div
              className={`${styles.projectDifficultyPill} ${
                styles['projectDifficultyPill' + capitalise(topic.difficulty)]
              }`}
            >
              {capitalise(topic.difficulty)}
            </div>
          </Col>
        </Row>
        <Row className={styles.projectLessonsText}>
          <Col xs={1}>
            <img src="/open-book.png" />
          </Col>
          <Col>
            {project.conceptsCountTotal} Lesson
            {project.conceptsCountTotal === 1 ? '' : 's'}
          </Col>
        </Row>
        {topic.dependencies.length > 0 ? (
          <Row className={styles.projectPrerequisitesText}>
            <Col xs={1}>
              <img src="/check-mark.png" />
            </Col>
            <Col>
              {topic.dependencies.length} Pre-requisite
              {topic.dependencies.length === 1 ? '' : 's'}
            </Col>
          </Row>
        ) : null}
        {project.isStarted ? (
          <Row>
            <Col>
              <div className={styles.projectProgressHeading}>Progress:</div>
              <ProjectProgressBar
                percentage={project.progressPercentage}
                timeEstimate={timeRemaining}
                isComplete={project.isComplete}
              />
            </Col>
          </Row>
        ) : (
          <Row className={styles.projectTimeToCompleteText}>
            <Col xs={1}>
              <img src="/clock.png" />
            </Col>
            <Col>{timeRemaining}</Col>
          </Row>
        )}
        {tags.length > 0 ? (
          <Row>
            <Col className={styles.projectTags}>
              {tags.map((tag) => (
                <div key={tag} className={styles.projectTagPill}>
                  <div>{tag}</div>
                </div>
              ))}
            </Col>
          </Row>
        ) : null}
      </div>
    </Col>
  );
};

type RoleFilter = TopicRole;
const RoleFilters: RoleFilter[] = [
  'all',
  'product',
  'marketing',
  'seo',
  'sales',
  'finance',
];
const isRoleFilter = (value: any): value is RoleFilter =>
  typeof value === 'string' && RoleFilters.some((r) => r === value);

type DifficultyFilter = TopicDifficulty | 'all';
const DifficultyFilters: DifficultyFilter[] = [
  'all',
  'beginner',
  'intermediate',
  'advanced',
];
const isDifficultyFilter = (value: any): value is DifficultyFilter =>
  typeof value === 'string' && DifficultyFilters.some((d) => d === value);

const defaultSearchFilter = '';
const defaultRoleFilter: typeof RoleFilters[number] = 'all';
const defaultDifficultyFilter: typeof DifficultyFilters[number] = 'all';

const STORED_PROJECTS_FILTER_KEY = 'supercooldata_dashboard_projects_filter';

const getPersistedFilters = () => {
  let persistedSearchFilter: string | undefined = undefined;
  let persistedRoleFilter: RoleFilter | undefined = undefined;
  let persistedDifficultyFilter: DifficultyFilter | undefined = undefined;

  const raw = getLS(STORED_PROJECTS_FILTER_KEY);
  if (typeof raw === 'string') {
    const parsed = JSON.parse(raw);
    if (parsed) {
      const {
        search: rawSearchFilter,
        role: rawRoleFilter,
        difficulty: rawDifficultyFilter,
      } = parsed;

      if (typeof rawSearchFilter === 'string' && rawSearchFilter.length > 0) {
        persistedSearchFilter = rawSearchFilter;
      }
      if (isRoleFilter(rawRoleFilter)) {
        persistedRoleFilter = rawRoleFilter;
      }
      if (isDifficultyFilter(rawDifficultyFilter)) {
        persistedDifficultyFilter = rawDifficultyFilter;
      }
    }
  }

  return {
    search: persistedSearchFilter,
    role: persistedRoleFilter,
    difficulty: persistedDifficultyFilter,
  };
};

const AuthenticatedDashboard: React.FC = () => {
  const router = useRouter();
  const { refresh: refreshStore, course } = useStore();
  const { refresh: refreshAuth } = useAuth();
  const persistedFilters = useMemo(getPersistedFilters, []);
  const [searchFilter, setSearchFilter] = useState(
    persistedFilters.search !== undefined
      ? persistedFilters.search
      : defaultSearchFilter
  );
  const [roleFilter, setRoleFilter] = useState<RoleFilter>(
    persistedFilters.role !== undefined
      ? persistedFilters.role
      : defaultRoleFilter
  );
  const [difficultyFilter, setDifficultyFilter] = useState<DifficultyFilter>(
    persistedFilters.difficulty !== undefined
      ? persistedFilters.difficulty
      : defaultDifficultyFilter
  );

  useEffect(() => {
    const newPersistedFilters: Partial<
      ReturnType<typeof getPersistedFilters>
    > = {};

    if (searchFilter !== defaultSearchFilter) {
      newPersistedFilters.search = searchFilter;
    }

    if (roleFilter !== defaultRoleFilter) {
      newPersistedFilters.role = roleFilter;
    }

    if (difficultyFilter !== defaultDifficultyFilter) {
      newPersistedFilters.difficulty = difficultyFilter;
    }

    setLS(STORED_PROJECTS_FILTER_KEY, JSON.stringify(newPersistedFilters));
  }, [searchFilter, roleFilter, difficultyFilter]);

  useEffect(() => {
    const onRouteChangeComplete = (url: string) => {
      if (url === '/dashboard') {
        refreshStore();
        refreshAuth();
      }
    };

    router.events.on('routeChangeComplete', onRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, [router.events, refreshStore, refreshAuth]);

  const [
    projectTopics,
    myProjectTopics,
    filteredProjectTopics,
  ] = useMemo(() => {
    const all =
      course !== undefined
        ? course.projects
            .map((project) => {
              const topic = course.topics.find((t) => t.id === project.topicId);
              return topic !== undefined ? { project, topic } : undefined;
            })
            .filter((x): x is Exclude<typeof x, undefined> => x !== undefined)
        : [];

    const searchTerms = searchFilter
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .map((x) => x.trim())
      .filter((x) => x.length > 0);

    const mine = all
      .filter(({ project }) => project.isStarted)
      .sort((a, b) =>
        a.project.isComplete && !b.project.isComplete
          ? 1
          : !a.project.isComplete && b.project.isComplete
          ? -1
          : 0
      );

    const filtered = all.filter(({ project, topic }) => {
      let success = true;

      const targets = [
        project.fullDescription,
        ...(project.isFree ? ['free'] : []),
        topic.label,
        topic.description,
        topic.difficulty,
        ...topic.concepts.map((concept) => concept.label),
        ...topic.roles,
        ...topic.tags,
        ...topic.skills,
      ]
        .map((x) => x.toLowerCase().trim())
        .filter((x) => x.length > 0);

      if (
        searchTerms.length > 0 &&
        !searchTerms.every((term) =>
          targets.some((target) => target.indexOf(term) >= 0)
        )
      ) {
        success = false;
      }

      if (
        roleFilter !== 'all' &&
        !topic.roles.includes(roleFilter) &&
        !topic.roles.includes('all')
      ) {
        success = false;
      }

      if (
        difficultyFilter !== 'all' &&
        topic.difficulty.toLowerCase() !== difficultyFilter.toLowerCase()
      ) {
        success = false;
      }

      return success;
    });

    return [all, mine, filtered];
  }, [course, searchFilter, roleFilter, difficultyFilter]);

  const onChangeSearchFilter = useCallback((event) => {
    setSearchFilter(event.target.value);
  }, []);

  const onChangeRoleFilter = useCallback((event) => {
    setRoleFilter(event.target.value);
  }, []);

  const onChangeDifficultyFilter = useCallback((event) => {
    setDifficultyFilter(event.target.value);
  }, []);

  const onClickShowAllProjects = useCallback(() => {
    setSearchFilter(defaultSearchFilter);
    setRoleFilter(defaultRoleFilter);
    setDifficultyFilter(defaultDifficultyFilter);
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  const onClickShowAllProjectsLink: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      onClickShowAllProjects();
    },
    [onClickShowAllProjects]
  );

  if (course === undefined) {
    return <Spinner />;
  }

  return (
    <>
      <Row className={styles.mainSection}>
        <Col>
          <Container>
            <Row noGutters={true} className={styles.topBar}>
              <Col className="text-center text-md-left">
                <Logo href="/dashboard" />
              </Col>
              <Col className={`d-none d-md-block ${styles.topSettings}`}>
                <div className={styles.signOutLink}>
                  <InstrumentedLink
                    internalHref="/settings"
                    label="dashboard_top_bar_settings_icon"
                    category={CATEGORIES.DASHBOARD}
                  >
                    <GearIcon width={2} height={2} />
                  </InstrumentedLink>
                </div>
              </Col>
            </Row>
            <PaymentDueBanner />
            <ReactivateSubscriptionBanner />
            {myProjectTopics.length > 0 ? (
              <>
                <Row>
                  <Col className={styles.projectsHeading}>My Projects</Col>
                </Row>
                <Row className={styles.myProjectsRow}>
                  {myProjectTopics.map(({ project, topic }) => (
                    <ProjectCard
                      key={project.topicId}
                      project={project}
                      topic={topic}
                    />
                  ))}
                </Row>
              </>
            ) : null}
            <Row>
              <Col className={styles.projectsHeading}>Projects</Col>
            </Row>
            <Row>
              <Col className={styles.projectCountText}>
                Showing
                {filteredProjectTopics.length === projectTopics.length
                  ? ' all'
                  : ''}{' '}
                {filteredProjectTopics.length} project
                {pluralise(filteredProjectTopics.length)}
                {filteredProjectTopics.length < projectTopics.length
                  ? ` out of ${projectTopics.length}`
                  : ''}
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6} className={styles.searchFilter}>
                <Form.Group controlId="filterProjectsForm.SearchText">
                  <Form.Label>&nbsp;</Form.Label>
                  <Form.Control
                    placeholder="Search"
                    value={searchFilter}
                    onChange={onChangeSearchFilter}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={3} className={styles.roleFilter}>
                <Form.Group controlId="filterProjectsForm.RoleSelect">
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    as="select"
                    value={roleFilter}
                    onChange={onChangeRoleFilter}
                  >
                    {RoleFilters.map((role) => (
                      <option key={role} value={role}>
                        {projectRoleLabel(role)}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} md={3} className={styles.difficultyFilter}>
                <Form.Group controlId="filterProjectsForm.DifficultySelect">
                  <Form.Label>Difficulty</Form.Label>
                  <Form.Control
                    as="select"
                    value={difficultyFilter}
                    onChange={onChangeDifficultyFilter}
                  >
                    {DifficultyFilters.map((difficulty) => (
                      <option key={difficulty} value={difficulty}>
                        {capitalise(difficulty)}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col className={styles.clearAllFiltersLink}>
                {searchFilter !== defaultSearchFilter ||
                roleFilter !== defaultRoleFilter ||
                difficultyFilter !== defaultDifficultyFilter ? (
                  <a href="#" onClick={onClickShowAllProjectsLink}>
                    Clear All Filters
                  </a>
                ) : (
                  <>&nbsp;</>
                )}
              </Col>
            </Row>
            {filteredProjectTopics.length > 0 ? (
              <Row>
                {filteredProjectTopics.map(({ project, topic }) => (
                  <ProjectCard
                    key={project.topicId}
                    project={project}
                    topic={topic}
                  />
                ))}
                {filteredProjectTopics.length < projectTopics.length ? (
                  <Col
                    md={4}
                    sm={6}
                    xs={12}
                    onClick={onClickShowAllProjects}
                    className={`${styles.projectCard} ${styles.showAllProjectsCard}`}
                  >
                    <div className={styles.projectIconContainer}>
                      <div className={styles.projectIcon}>&nbsp;</div>
                    </div>
                    <div className={styles.projectCardInner}>
                      <div>
                        Show all {projectTopics.length} project
                        {pluralise(projectTopics.length)}
                      </div>
                    </div>
                  </Col>
                ) : null}
              </Row>
            ) : (
              <Row>
                <Col>
                  <div>Could not find any projects to display.</div>
                  {projectTopics.length > 0 ? (
                    <div>
                      <a href="#" onClick={onClickShowAllProjectsLink}>
                        Show all {projectTopics.length} project
                        {pluralise(projectTopics.length)}
                      </a>
                    </div>
                  ) : null}
                </Col>
              </Row>
            )}
            <Row className={`mx-0 d-md-none ${styles.bottomBar}`}>
              <Col className={styles.mobileSettings}>
                <div className={styles.signOutLink}>
                  <InstrumentedLink
                    internalHref="/settings"
                    label="dashboard_mobile_settings_icon"
                    category={CATEGORIES.DASHBOARD}
                  >
                    Settings
                  </InstrumentedLink>
                </div>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
      <Footer />
    </>
  );
};

const Dashboard: React.FC = () => {
  useEffect(() => {
    sendPageHitEvent({ category: 'dashboard' });
  }, []);

  return (
    <Container fluid>
      <Head>
        <title>Learn SQL analytics - supercooldata</title>
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <AuthPage>
        <AuthenticatedDashboard />
      </AuthPage>
    </Container>
  );
};

export default Dashboard;
